export type Language = 'sv' | 'en';

export interface Labels {
  // General
  appName: string;
  trackYourHealth: string;
  createYourAccount: string;
  loading: string;
  save: string;
  saving: string;
  cancel: string;
  confirm: string;
  delete: string;
  edit: string;
  actions: string;

  // Auth
  login: string;
  logIn: string;
  register: string;
  email: string;
  password: string;
  name: string;
  noAccount: string;
  hasAccount: string;
  loginFailed: string;
  registerFailed: string;
  createAccount: string;

  // Registration pending
  registrationPendingTitle: string;
  registrationPendingBody: string;
  passwordRequirements: string;

  // Profile
  profile: string;
  height: string;
  dateOfBirth: string;
  gender: string;
  male: string;
  female: string;
  other: string;
  targetWeight: string;
  targetDate: string;
  profileUpdated: string;
  profileFailed: string;
  logOut: string;
  saveProfile: string;
  adminPortal: string;

  // Navigation
  home: string;
  meals: string;
  weight: string;
  billing: string;

  // Dashboard
  hej: string;
  weightProgress: string;
  start: string;
  now: string;
  target: string;
  remaining: string;
  todaysCalories: string;
  logMeal: string;
  logWeight: string;
  thisWeek: string;
  avgKcalDay: string;
  mealsLogged: string;
  weightChange: string;

  // Meals
  logYourMeal: string;
  mealType: string;
  breakfast: string;
  lunch: string;
  dinner: string;
  snack: string;
  describeFood: string;
  estimatedCalories: string;
  uploadPhoto: string;
  logging: string;
  history: string;
  searchFood: string;
  recentMeals: string;
  dailyCalories: string;
  total7d: string;
  avgDay: string;
  kcal: string;
  noMealsYet: string;
  noProductsFound: string;
  analyzePhotoFailed: string;
  analyzeTextFailed: string;
  analyzeNoResults: string;
  analyzeFood: string;
  logMealFailed: string;
  searchFoodProducts: string;
  searchFoodBrowseNote: string;

  // Weight
  weightOverTime: string;
  noWeightData: string;
  change: string;
  current: string;
  kgToTarget: string;
  date: string;
  saved: string;
  currentWeight: string;

  // Macros
  protein: string;
  carbs: string;
  fat: string;

  // Barcode
  scanBarcode: string;
  scanning: string;
  barcodeNotFound: string;
  cameraPermissionDenied: string;
  barcodeScannerNotSupported: string;
  amount: string;
  addAndScanNext: string;
  addAndSearchNext: string;
  addAndDone: string;
  doneScanItems: string;

  // Per 100g
  per100g: string;
  serving: string;

  // Admin
  adminUsers: string;
  adminPortalTitle: string;
  status: string;
  statusActive: string;
  statusPending: string;
  statusSuspended: string;
  approve: string;
  suspend: string;
  reactivate: string;
  filterByStatus: string;
  allStatuses: string;
  noUsersFound: string;
  confirmDeleteUser: string;
  userDeleted: string;
  userApproved: string;
  userSuspended: string;
  userReactivated: string;
  adminActionFailed: string;
  isAdmin: string;
  subscriptionStatus: string;
  refund: string;
  refundIssued: string;
  refundFailed: string;
  resetPassword: string;
  newPassword: string;
  generatePassword: string;
  passwordReset: string;
  passwordResetFailed: string;
  makeAdmin: string;
  removeAdmin: string;
  adminToggled: string;
  adminToggleFailed: string;
  copyToClipboard: string;

  // Meal CRUD
  confirmDeleteMeal: string;
  mealDeleted: string;
  mealUpdated: string;
  mealDeleteFailed: string;
  mealUpdateFailed: string;

  // Weight CRUD
  confirmDeleteWeight: string;
  weightDeleted: string;
  weightDeleteFailed: string;

  // Billing
  billingTitle: string;
  subscribeNow: string;
  activeSubscription: string;
  noSubscription: string;
  subscribeFailed: string;
  paymentSuccessful: string;
  paymentCanceled: string;

  // Activity level
  activityLevel: string;
  actSedentary: string;
  actLight: string;
  actModerate: string;
  actActive: string;
  actVeryActive: string;

  // Repeat yesterday
  repeatYesterday: string;

  // Insights
  todaysInsights: string;
  insightsUnavailable: string;
  refreshInsights: string;
  weeklyReport: string;
  weeklyInsightsUnavailable: string;
  refreshWeeklyInsights: string;

  // Streaks
  dayStreak: string;
  bestStreak: string;
  logTodayStreak: string;

  // Search
  recentSearches: string;

  // Error boundary
  somethingWentWrong: string;
  unexpectedError: string;
  goToStart: string;

  // Meal history errors
  couldNotLoadMeals: string;
  retry: string;

  // Barcode scanner status
  lookingUpProduct: string;
  scanningBarcode: string;

  // Weight errors
  weightLogFailed: string;
  weightFetchFailed: string;
  dashboardLoadFailed: string;

  // Meal date
  mealDate: string;

  // Country
  country: string;
  countryAll: string;

  // Diet plan
  dietPlan: string;
  dietStandard: string;
  diet52: string;
  fastingDay: string;
  normalDay: string;
  selectFastingDays: string;
  monday: string;
  tuesday: string;
  wednesday: string;
  thursday: string;
  friday: string;
  saturday: string;
  sunday: string;

  // Voice
  voiceRecord: string;
  voiceRecording: string;
  voiceTranscribing: string;
  voiceTranscribeFailed: string;
  micPermissionDenied: string;

  // Appearance
  darkMode: string;
}

const sv: Labels = {
  appName: 'MyHealth',
  trackYourHealth: 'Spåra din hälsoresa',
  createYourAccount: 'Skapa ditt konto',
  loading: 'Laddar...',
  save: 'Spara',
  saving: 'Sparar...',
  cancel: 'Avbryt',
  confirm: 'Bekräfta',
  delete: 'Ta bort',
  edit: 'Redigera',
  actions: 'Åtgärder',

  login: 'Logga in',
  logIn: 'Logga in',
  register: 'Registrera',
  email: 'E-post',
  password: 'Lösenord',
  name: 'Namn',
  noAccount: 'Har du inget konto?',
  hasAccount: 'Har du redan ett konto?',
  loginFailed: 'Fel e-post eller lösenord',
  registerFailed: 'Registreringen misslyckades. E-posten kan redan vara registrerad.',
  createAccount: 'Skapa konto',

  registrationPendingTitle: 'Registrering skickad!',
  registrationPendingBody: 'Ditt konto väntar på godkännande av en administratör. Du får ett e-postmeddelande när det är godkänt.',
  passwordRequirements: 'Minst 8 tecken, 1 versal, 1 siffra och 1 specialtecken',

  profile: 'Profil',
  height: 'Längd (cm)',
  dateOfBirth: 'Födelsedatum',
  gender: 'Kön',
  male: 'Man',
  female: 'Kvinna',
  other: 'Annat',
  targetWeight: 'Målvikt (kg)',
  targetDate: 'Måldatum',
  profileUpdated: 'Profilen uppdaterad!',
  profileFailed: 'Kunde inte uppdatera profilen',
  logOut: 'Logga ut',
  saveProfile: 'Spara profil',
  adminPortal: 'Adminportal',

  home: 'Hem',
  meals: 'Mat',
  weight: 'Vikt',
  billing: 'Prenumeration',

  hej: 'Hej',
  weightProgress: 'Viktframsteg',
  start: 'Start',
  now: 'Nu',
  target: 'Mål',
  remaining: 'kvar',
  todaysCalories: 'Dagens kalorier',
  logMeal: 'Logga mat',
  logWeight: 'Logga vikt',
  thisWeek: 'Denna vecka',
  avgKcalDay: 'Snitt kcal/dag',
  mealsLogged: 'Måltider loggade',
  weightChange: 'Viktförändring',

  logYourMeal: 'Logga din måltid',
  mealType: 'Måltidstyp',
  breakfast: 'Frukost',
  lunch: 'Lunch',
  dinner: 'Middag',
  snack: 'Mellanmål',
  describeFood: 'Beskriv din mat',
  estimatedCalories: 'Uppskattade kalorier',
  uploadPhoto: 'Ladda upp foto',
  logging: 'Loggar...',
  history: 'Historik',
  searchFood: 'Sök mat',
  recentMeals: 'Senaste måltider',
  dailyCalories: 'Dagliga kalorier',
  total7d: 'Totalt (7d)',
  avgDay: 'Snitt/dag',
  kcal: 'kcal',
  noMealsYet: 'Inga måltider loggade än. Börja med att logga din första måltid!',
  noProductsFound: 'Inga produkter hittades. Försök med ett annat sökord.',
  analyzePhotoFailed: 'Kunde inte analysera fotot. Ange detaljer manuellt.',
  analyzeTextFailed: 'Kunde inte analysera texten. Ange kalorier manuellt.',
  analyzeNoResults: 'Kunde inte känna igen maten. Ange kalorier manuellt.',
  analyzeFood: 'Analysera',
  logMealFailed: 'Fel vid loggning av måltid. Försök igen.',
  searchFoodProducts: 'Sök efter livsmedelsprodukter...',
  searchFoodBrowseNote: 'Bläddra bland näringsinformation. För att logga mat, använd fliken Logga måltid.',

  weightOverTime: 'Vikt över tid',
  noWeightData: 'Ingen viktdata än. Logga din vikt för att se grafen!',
  change: 'Förändring',
  current: 'Nuvarande',
  kgToTarget: 'kg till mål',
  date: 'Datum',
  saved: 'Sparad!',
  currentWeight: 'Nuvarande vikt (kg)',

  protein: 'Protein',
  carbs: 'Kolhydrater',
  fat: 'Fett',

  scanBarcode: 'Skanna streckkod',
  scanning: 'Skannar...',
  barcodeNotFound: 'Produkten hittades inte. Försök med en annan streckkod.',
  cameraPermissionDenied: 'Kameraåtkomst nekad. Tillåt kameran i webbläsarinställningarna.',
  barcodeScannerNotSupported: 'Streckkodsläsaren stöds inte i denna webbläsare. Prova Chrome eller Safari.',
  amount: 'Mängd',
  addAndScanNext: 'Lägg till & skanna nästa',
  addAndSearchNext: 'Lägg till & sök igen',
  addAndDone: 'Lägg till & klar',
  doneScanItems: 'Klar ({n} varor)',

  per100g: 'per 100g',
  serving: 'portion',

  adminUsers: 'Användare',
  adminPortalTitle: 'Adminportal',
  status: 'Status',
  statusActive: 'Aktiv',
  statusPending: 'Väntar',
  statusSuspended: 'Avstängd',
  approve: 'Godkänn',
  suspend: 'Stäng av',
  reactivate: 'Återaktivera',
  filterByStatus: 'Filtrera efter status',
  allStatuses: 'Alla statusar',
  noUsersFound: 'Inga användare hittades.',
  confirmDeleteUser: 'Är du säker på att du vill ta bort denna användare? Denna åtgärd kan inte ångras.',
  userDeleted: 'Användaren har tagits bort.',
  userApproved: 'Användaren har godkänts.',
  userSuspended: 'Användaren har stängts av.',
  userReactivated: 'Användaren har återaktiverats.',
  adminActionFailed: 'Åtgärden misslyckades. Försök igen.',
  isAdmin: 'Admin',
  subscriptionStatus: 'Prenumerationsstatus',
  refund: 'Återbetalning',
  refundIssued: 'Återbetalning utfärdad.',
  refundFailed: 'Återbetalning misslyckades.',
  resetPassword: 'Återställ lösenord',
  newPassword: 'Nytt lösenord',
  generatePassword: 'Generera',
  passwordReset: 'Lösenord återställt',
  passwordResetFailed: 'Kunde inte återställa lösenord',
  makeAdmin: 'Gör till admin',
  removeAdmin: 'Ta bort adminbehörighet',
  adminToggled: 'Adminstatus uppdaterad',
  adminToggleFailed: 'Kunde inte uppdatera adminstatus',
  copyToClipboard: 'Kopiera',

  confirmDeleteMeal: 'Ta bort denna måltid?',
  mealDeleted: 'Måltid borttagen',
  mealUpdated: 'Måltid uppdaterad',
  mealDeleteFailed: 'Kunde inte ta bort måltid',
  mealUpdateFailed: 'Kunde inte uppdatera måltid',
  confirmDeleteWeight: 'Ta bort denna viktmätning?',
  weightDeleted: 'Viktmätning borttagen',
  weightDeleteFailed: 'Kunde inte ta bort viktmätning',

  billingTitle: 'Prenumeration',
  subscribeNow: 'Prenumerera nu',
  activeSubscription: 'Aktiv prenumeration',
  noSubscription: 'Ingen aktiv prenumeration',
  subscribeFailed: 'Kunde inte starta prenumerationsprocessen.',
  paymentSuccessful: 'Betalning lyckades! Välkommen som prenumerant.',
  paymentCanceled: 'Betalning avbruten.',

  activityLevel: 'Aktivitetsnivå',
  actSedentary: 'Stillasittande (kontorsjobb, lite träning)',
  actLight: 'Lätt aktiv (tränar 1–3×/vecka)',
  actModerate: 'Måttligt aktiv (tränar 3–5×/vecka)',
  actActive: 'Aktiv (hård träning 6–7×/vecka)',
  actVeryActive: 'Mycket aktiv (fysiskt jobb + träning)',

  repeatYesterday: 'Samma som igår',

  todaysInsights: 'Dagens insikter',
  insightsUnavailable: 'Insikter otillgängliga — tryck uppdatera för att försöka igen.',
  refreshInsights: 'Uppdatera insikter',
  weeklyReport: 'Veckorapport',
  weeklyInsightsUnavailable: 'Veckoinsikter otillgängliga',
  refreshWeeklyInsights: 'Uppdatera veckoinsikter',
  dayStreak: '{n} dagars svit!',
  bestStreak: 'Bäst: {n} dagar',
  logTodayStreak: 'Logga en måltid idag för att hålla din svit igång',
  recentSearches: 'Senaste',

  somethingWentWrong: 'Något gick fel',
  unexpectedError: 'Ett oväntat fel uppstod. Försök igen.',
  goToStart: 'Gå till start',
  couldNotLoadMeals: 'Kunde inte ladda måltider. Kontrollera din anslutning och försök igen.',
  retry: 'Försök igen',
  lookingUpProduct: 'Söker produkt...',
  scanningBarcode: 'Skannar...',
  weightLogFailed: 'Kunde inte logga vikt. Försök igen.',
  weightFetchFailed: 'Kunde inte hämta viktdata. Kontrollera din anslutning.',
  dashboardLoadFailed: 'Kunde inte ladda dashboard. Kontrollera din anslutning och försök igen.',

  mealDate: 'Datum',

  country: 'Land',
  countryAll: 'Alla / Globalt',

  dietPlan: 'Kostplan',
  dietStandard: 'Standard',
  diet52: '5:2',
  fastingDay: 'Fastedag',
  normalDay: 'Normal dag',
  selectFastingDays: 'Välj fastedagar',
  monday: 'Måndag',
  tuesday: 'Tisdag',
  wednesday: 'Onsdag',
  thursday: 'Torsdag',
  friday: 'Fredag',
  saturday: 'Lördag',
  sunday: 'Söndag',

  voiceRecord: 'Tala',
  voiceRecording: 'Spelar in...',
  voiceTranscribing: 'Transkriberar...',
  voiceTranscribeFailed: 'Kunde inte transkribera ljud. Försök igen.',
  micPermissionDenied: 'Mikrofonåtkomst nekad. Tillåt mikrofonen i webbläsarinställningarna.',

  darkMode: 'Mörkt läge',
};

const en: Labels = {
  appName: 'MyHealth',
  trackYourHealth: 'Track your health journey',
  createYourAccount: 'Create your account',
  loading: 'Loading...',
  save: 'Save',
  saving: 'Saving...',
  cancel: 'Cancel',
  confirm: 'Confirm',
  delete: 'Delete',
  edit: 'Edit',
  actions: 'Actions',

  login: 'Login',
  logIn: 'Log in',
  register: 'Register',
  email: 'Email',
  password: 'Password',
  name: 'Name',
  noAccount: "Don't have an account?",
  hasAccount: 'Already have an account?',
  loginFailed: 'Incorrect email or password',
  registerFailed: 'Registration failed. Email may already be in use.',
  createAccount: 'Create Account',

  registrationPendingTitle: 'Registration submitted!',
  registrationPendingBody: 'Your account is pending approval by an administrator. You will receive an email once approved.',
  passwordRequirements: 'At least 8 characters, 1 uppercase, 1 digit, and 1 special character',

  profile: 'Profile',
  height: 'Height (cm)',
  dateOfBirth: 'Date of Birth',
  gender: 'Gender',
  male: 'Male',
  female: 'Female',
  other: 'Other',
  targetWeight: 'Target Weight (kg)',
  targetDate: 'Target Date',
  profileUpdated: 'Profile updated!',
  profileFailed: 'Failed to update profile',
  logOut: 'Log Out',
  saveProfile: 'Save Profile',
  adminPortal: 'Admin Portal',

  home: 'Home',
  meals: 'Meals',
  weight: 'Weight',
  billing: 'Billing',

  hej: 'Hi',
  weightProgress: 'Weight Progress',
  start: 'Start',
  now: 'Now',
  target: 'Target',
  remaining: 'remaining',
  todaysCalories: "Today's Calories",
  logMeal: 'Log Meal',
  logWeight: 'Log Weight',
  thisWeek: 'This Week',
  avgKcalDay: 'Avg kcal/day',
  mealsLogged: 'Meals logged',
  weightChange: 'Weight change',

  logYourMeal: 'Log Your Meal',
  mealType: 'Meal Type',
  breakfast: 'Breakfast',
  lunch: 'Lunch',
  dinner: 'Dinner',
  snack: 'Snack',
  describeFood: 'Describe your food',
  estimatedCalories: 'Estimated Calories',
  uploadPhoto: 'Upload Photo',
  logging: 'Logging...',
  history: 'History',
  searchFood: 'Search Food',
  recentMeals: 'Recent Meals',
  dailyCalories: 'Daily Calories',
  total7d: 'Total (7d)',
  avgDay: 'Avg/Day',
  kcal: 'kcal',
  noMealsYet: 'No meals logged yet. Start by logging your first meal!',
  noProductsFound: 'No products found. Try a different search term.',
  analyzePhotoFailed: 'Could not analyze photo. Please enter details manually.',
  analyzeTextFailed: 'Could not analyze text. Please enter calories manually.',
  analyzeNoResults: 'Could not recognize the food. Please enter calories manually.',
  analyzeFood: 'Analyze',
  logMealFailed: 'Error logging meal. Please try again.',
  searchFoodProducts: 'Search for food products...',
  searchFoodBrowseNote: 'Browse nutrition info. To log food, use the Log Meal tab.',

  weightOverTime: 'Weight Over Time',
  noWeightData: 'No weight data yet. Log your weight to see the graph!',
  change: 'Change',
  current: 'Current',
  kgToTarget: 'kg to target',
  date: 'Date',
  saved: 'Saved!',
  currentWeight: 'Current Weight (kg)',

  protein: 'Protein',
  carbs: 'Carbs',
  fat: 'Fat',

  scanBarcode: 'Scan barcode',
  scanning: 'Scanning...',
  barcodeNotFound: 'Product not found. Try a different barcode.',
  cameraPermissionDenied: 'Camera access denied. Please allow camera in browser settings.',
  barcodeScannerNotSupported: 'Barcode scanner not supported in this browser. Try Chrome or Safari.',
  amount: 'Amount',
  addAndScanNext: 'Add & Scan Next',
  addAndSearchNext: 'Add & Search Again',
  addAndDone: 'Add & Done',
  doneScanItems: 'Done ({n} items)',

  per100g: 'per 100g',
  serving: 'serving',

  adminUsers: 'Users',
  adminPortalTitle: 'Admin Portal',
  status: 'Status',
  statusActive: 'Active',
  statusPending: 'Pending',
  statusSuspended: 'Suspended',
  approve: 'Approve',
  suspend: 'Suspend',
  reactivate: 'Reactivate',
  filterByStatus: 'Filter by status',
  allStatuses: 'All statuses',
  noUsersFound: 'No users found.',
  confirmDeleteUser: 'Are you sure you want to delete this user? This action cannot be undone.',
  userDeleted: 'User deleted.',
  userApproved: 'User approved.',
  userSuspended: 'User suspended.',
  userReactivated: 'User reactivated.',
  adminActionFailed: 'Action failed. Please try again.',
  isAdmin: 'Admin',
  subscriptionStatus: 'Subscription status',
  refund: 'Refund',
  refundIssued: 'Refund issued.',
  refundFailed: 'Refund failed.',
  resetPassword: 'Reset Password',
  newPassword: 'New Password',
  generatePassword: 'Generate',
  passwordReset: 'Password reset successfully',
  passwordResetFailed: 'Failed to reset password',
  makeAdmin: 'Make Admin',
  removeAdmin: 'Remove Admin',
  adminToggled: 'Admin status updated',
  adminToggleFailed: 'Failed to update admin status',
  copyToClipboard: 'Copy',

  confirmDeleteMeal: 'Delete this meal?',
  mealDeleted: 'Meal deleted',
  mealUpdated: 'Meal updated',
  mealDeleteFailed: 'Failed to delete meal',
  mealUpdateFailed: 'Failed to update meal',
  confirmDeleteWeight: 'Delete this weight entry?',
  weightDeleted: 'Weight entry deleted',
  weightDeleteFailed: 'Failed to delete weight entry',

  billingTitle: 'Billing',
  subscribeNow: 'Subscribe Now',
  activeSubscription: 'Active Subscription',
  noSubscription: 'No active subscription',
  subscribeFailed: 'Could not start subscription process.',
  paymentSuccessful: 'Payment successful! Welcome to premium.',
  paymentCanceled: 'Payment canceled.',

  activityLevel: 'Activity Level',
  actSedentary: 'Sedentary (desk job, little exercise)',
  actLight: 'Lightly active (exercise 1–3×/week)',
  actModerate: 'Moderately active (exercise 3–5×/week)',
  actActive: 'Active (hard exercise 6–7×/week)',
  actVeryActive: 'Very active (physical job + exercise)',

  repeatYesterday: 'Same as yesterday',

  todaysInsights: "Today's Insights",
  insightsUnavailable: 'Insights unavailable — tap refresh to try again.',
  refreshInsights: 'Refresh insights',
  weeklyReport: 'Weekly report',
  weeklyInsightsUnavailable: 'Weekly insights unavailable',
  refreshWeeklyInsights: 'Refresh weekly insights',
  dayStreak: '{n} day streak!',
  bestStreak: 'Best: {n} days',
  logTodayStreak: 'Log a meal today to keep your streak going',
  recentSearches: 'Recent',

  somethingWentWrong: 'Something went wrong',
  unexpectedError: 'An unexpected error occurred. Please try again.',
  goToStart: 'Go to start',
  couldNotLoadMeals: 'Could not load meals. Check your connection and try again.',
  retry: 'Retry',
  lookingUpProduct: 'Looking up product...',
  scanningBarcode: 'Scanning...',
  weightLogFailed: 'Failed to log weight. Please try again.',
  weightFetchFailed: 'Could not load weight data. Check your connection.',
  dashboardLoadFailed: 'Could not load dashboard. Check your connection and try again.',

  mealDate: 'Date',

  country: 'Country',
  countryAll: 'All / Global',

  dietPlan: 'Diet Plan',
  dietStandard: 'Standard',
  diet52: '5:2',
  fastingDay: 'Fasting day',
  normalDay: 'Normal day',
  selectFastingDays: 'Select fasting days',
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  sunday: 'Sunday',

  voiceRecord: 'Voice',
  voiceRecording: 'Recording...',
  voiceTranscribing: 'Transcribing...',
  voiceTranscribeFailed: 'Could not transcribe audio. Please try again.',
  micPermissionDenied: 'Microphone access denied. Please allow microphone in browser settings.',

  darkMode: 'Dark mode',
};

const labels: Record<Language, Labels> = { sv, en };
export default labels;
